import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, apiGet } from "../../api";
import { Button } from "../../components/ui/button";
import { ChevronDown, ChevronRight, AlertTriangle, Trash2 } from "lucide-react";
import { Badge, fmtPrice } from "./shared";

/**
 * Review Queue tab — submissions held back by the >=95% SKU match check.
 *
 * Each held submission lists the rows that didn't match an existing
 * price_history SKU for the supplier. For each flagged row the reviewer
 * must pick exactly one of:
 *   • "Approve as new SKU"  → action=approve_new
 *   • "Map to existing SKU" → action=fix + canonical_sku (pre-filled with
 *                              the suggested closest match if any)
 *
 * The Submit button is disabled until EVERY flagged row has a decision.
 * Non-flagged rows are shown read-only and don't need a decision.
 */
export default function ReviewQueueTab({ onResolved }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiGet("/portal/admin/review-queue", { params: { limit: 100 } });
      setItems(data?.items || []);
    } catch (e) {
      toast.error("Failed to load review queue");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Accept EVERY flagged row in EVERY in-review submission as a new SKU. Clears
  // the whole queue server-side (not just the loaded page) — for queues too
  // large to reconcile row-by-row.
  const bulkApproveAll = async () => {
    setBulkBusy(true);
    try {
      const res = await axios.post(`${API}/portal/admin/review-queue/approve-all`);
      const d = res.data || {};
      toast.success(
        `Approved ${d.submissions_approved} submission(s), ${d.price_history_rows_inserted} rows written`
        + (d.submissions_failed ? ` · ${d.submissions_failed} failed` : ""),
      );
      setConfirmBulk(false);
      await load();
      if (onResolved) onResolved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk approve failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const handleResolved = (submissionId) => {
    setItems(prev => prev.filter(s => s.id !== submissionId));
    setExpanded(null);
    if (onResolved) onResolved();
  };

  return (
    <div className="card" data-testid="review-queue-tab">
      <div style={{ marginBottom: "1rem" }}>
        <h3 style={{ color: "var(--octopus-ice)", fontSize: "1rem", marginBottom: "0.35rem" }}>
          SKU reconciliation queue
        </h3>
        <p style={{ color: "var(--octopus-voltage)", opacity: 0.75, fontSize: "0.85rem" }}>
          Submissions held back because one or more rows don't match an existing SKU on file
          for the supplier (≥95% similarity threshold). Resolve every flagged row before the
          submission flows on to the Records Updater. <strong>Approving records the price in
          PriceGuard</strong> — an Asana set-up ticket is only ever raised for items on the ICM list.
        </p>
      </div>

      {!loading && items.length > 0 && (
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
          {confirmBulk ? (
            <>
              <span style={{ fontSize: "0.82rem", color: "#fb923c" }}>
                Accept every flagged row as a new SKU in PriceGuard? (Asana tickets only for ICM-list items.)
              </span>
              <Button className="btn btn-primary" onClick={bulkApproveAll} disabled={bulkBusy} data-testid="bulk-approve-confirm">
                {bulkBusy ? "Approving…" : "Yes, approve all"}
              </Button>
              <Button onClick={() => setConfirmBulk(false)} disabled={bulkBusy} data-testid="bulk-approve-cancel"
                style={{ background: "transparent", border: "1px solid rgba(96,240,248,0.2)", color: "var(--octopus-voltage)", padding: "0.3rem 0.75rem", borderRadius: "6px", cursor: "pointer", fontSize: "0.82rem" }}>
                Cancel
              </Button>
            </>
          ) : (
            <Button onClick={() => setConfirmBulk(true)} data-testid="bulk-approve-all"
              style={{ background: "rgba(24,240,184,0.12)", border: "1px solid #18F0B8", color: "#18F0B8", padding: "0.4rem 0.9rem", borderRadius: "8px", cursor: "pointer", fontSize: "0.85rem", fontWeight: 600 }}>
              Approve all as new
            </Button>
          )}
        </div>
      )}

      {loading && <div style={{ padding: "1rem", color: "var(--octopus-voltage)" }} data-testid="review-queue-loading">Loading…</div>}

      {!loading && items.length === 0 && (
        <div data-testid="review-queue-empty" style={{
          padding: "2rem", textAlign: "center", color: "var(--octopus-voltage)",
          opacity: 0.7, fontSize: "0.9rem",
        }}>
          The review queue is empty. Approved submissions whose SKUs all match the existing
          price_history flow straight through.
        </div>
      )}

      {!loading && items.map(sub => (
        <SubmissionCard
          key={sub.id}
          sub={sub}
          isExpanded={expanded === sub.id}
          onToggle={() => setExpanded(expanded === sub.id ? null : sub.id)}
          onResolved={() => handleResolved(sub.id)}
        />
      ))}
    </div>
  );
}


/** One submission in the queue — header + expandable per-row reconciliation. */
function SubmissionCard({ sub, isExpanded, onToggle, onResolved }) {
  const flaggedCount = useMemo(
    () => (sub.rows || []).filter(r => r?._review?.needs_review).length,
    [sub.rows],
  );

  return (
    <div data-testid={`review-queue-card-${sub.id}`} style={{
      border: "1px solid rgba(96,240,248,0.12)", borderRadius: "10px",
      marginBottom: "0.75rem", overflow: "hidden",
    }}>
      <button
        onClick={onToggle}
        data-testid={`review-queue-toggle-${sub.id}`}
        style={{
          width: "100%", padding: "0.85rem 1rem", background: "rgba(96,240,248,0.04)",
          border: "none", color: "inherit", cursor: "pointer", display: "flex",
          alignItems: "center", gap: "0.65rem", textAlign: "left",
        }}
      >
        {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        <AlertTriangle size={16} color="#fb923c" />
        <div style={{ flex: 1 }}>
          <div style={{ color: "var(--octopus-ice)", fontWeight: 600, fontSize: "0.95rem" }}>
            {sub.supplier_name || sub.supplier_id}
            <span style={{ marginLeft: "0.5rem", color: "var(--octopus-voltage)", opacity: 0.7, fontWeight: 400, fontSize: "0.82rem" }}>
              {sub.reference || ""}
            </span>
          </div>
          <div style={{ fontSize: "0.78rem", color: "var(--octopus-voltage)", opacity: 0.7, marginTop: "0.2rem" }}>
            Approved {sub.reviewed_at ? new Date(sub.reviewed_at).toLocaleString() : "—"} · {sub.sku_count || (sub.rows || []).length} rows · {flaggedCount} need review
          </div>
        </div>
        <Badge
          bg="rgba(251,146,60,0.18)"
          fg="#fb923c"
          border="#fb923c"
          label={`${flaggedCount} flagged`}
        />
      </button>

      {isExpanded && (
        <ReconcileForm sub={sub} onResolved={onResolved} />
      )}
    </div>
  );
}


/** Per-row decision form. Holds every flagged row's action + canonical_sku. */
function ReconcileForm({ sub, onResolved }) {
  const flaggedRows = useMemo(
    () => (sub.rows || [])
      .map((r, idx) => ({ row: r, idx }))
      .filter(({ row }) => row?._review?.needs_review),
    [sub.rows],
  );

  // decisions[idx] = { action: "approve_new" | "fix", canonical_sku: string }
  // Only pre-select "fix" when similarity is high enough to be a genuine
  // suggestion (>=60%). Lower scores just show the closest match as context
  // but default to "approve_new" so the reviewer isn't nudged toward a wrong mapping.
  const FIX_PRESELECTOR_THRESHOLD = 0.60;
  const [decisions, setDecisions] = useState(() => {
    const initial = {};
    for (const { row, idx } of flaggedRows) {
      const cm = row._review?.closest_match;
      const strongMatch = cm && (row._review?.similarity ?? 0) >= FIX_PRESELECTOR_THRESHOLD;
      initial[idx] = {
        action: strongMatch ? "fix" : "approve_new",
        canonical_sku: cm?.sku || "",
      };
    }
    return initial;
  });

  const [submitting, setSubmitting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [approvingAll, setApprovingAll] = useState(false);

  // Accept every flagged row in THIS submission as a new SKU, in one call.
  const handleApproveAllNew = async () => {
    if (approvingAll) return;
    setApprovingAll(true);
    try {
      await axios.post(`${API}/portal/admin/review-queue/${sub.id}/approve-all`);
      toast.success(`Approved all ${flaggedRows.length} row(s) as new`);
      onResolved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Approve all failed");
    } finally {
      setApprovingAll(false);
    }
  };

  const setDecision = (idx, patch) => {
    setDecisions(prev => ({ ...prev, [idx]: { ...prev[idx], ...patch } }));
  };

  // All flagged rows have a complete decision?
  const ready = flaggedRows.every(({ idx }) => {
    const d = decisions[idx];
    if (!d) return false;
    if (d.action === "fix") return !!(d.canonical_sku || "").trim();
    return d.action === "approve_new";
  });

  const handleSubmit = async () => {
    if (!ready || submitting) return;
    setSubmitting(true);
    try {
      const resolutions = flaggedRows.map(({ idx }) => {
        const d = decisions[idx];
        const out = { row_index: idx, action: d.action };
        if (d.action === "fix") out.canonical_sku = d.canonical_sku.trim();
        return out;
      });
      await axios.post(`${API}/portal/admin/review-queue/${sub.id}/resolve`, { resolutions });
      toast.success(`Reconciliation submitted — ${resolutions.length} row(s) resolved`);
      onResolved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Resolve failed");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/portal/admin/review-queue/${sub.id}`);
      toast.success("Submission deleted");
      onResolved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
      setConfirmDelete(false);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div style={{ padding: "1rem", background: "rgba(0,0,0,0.15)" }}>
      <div style={{ marginBottom: "0.85rem", fontSize: "0.85rem", color: "var(--octopus-voltage)", opacity: 0.85 }}>
        {flaggedRows.length} row{flaggedRows.length !== 1 ? "s" : ""} to reconcile. Pick a decision
        for each, then submit.
      </div>

      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
          <thead>
            <tr style={{ color: "var(--octopus-voltage)", opacity: 0.7, borderBottom: "1px solid rgba(96,240,248,0.12)" }}>
              <th style={{ textAlign: "left", padding: "0.5rem", width: "26%" }}>Submitted SKU</th>
              <th style={{ textAlign: "left", padding: "0.5rem", width: "32%" }}>Closest match (similarity)</th>
              <th style={{ textAlign: "left", padding: "0.5rem", width: "42%" }}>Decision</th>
            </tr>
          </thead>
          <tbody>
            {flaggedRows.map(({ row, idx }) => {
              const cm = row._review?.closest_match;
              const sim = row._review?.similarity;
              const d = decisions[idx] || {};
              return (
                <tr key={idx} data-testid={`review-row-${idx}`} style={{ borderBottom: "1px solid rgba(96,240,248,0.05)" }}>
                  <td style={{ padding: "0.6rem 0.5rem", verticalAlign: "top" }}>
                    <div style={{ fontFamily: "monospace", color: "var(--octopus-ice)", fontWeight: 600 }}>{row.sku}</div>
                    <div style={{ fontSize: "0.78rem", color: "var(--octopus-voltage)", opacity: 0.65, marginTop: "0.2rem" }}>
                      {row.product_description}
                    </div>
                    <div style={{ marginTop: "0.35rem", fontSize: "0.78rem", color: "var(--octopus-voltage)", opacity: 0.6 }}>
                      Price: {fmtPrice(row.new_price, sub.currency)}
                    </div>
                  </td>
                  <td style={{ padding: "0.6rem 0.5rem", verticalAlign: "top" }}>
                    {cm ? (
                      <>
                        <div style={{ fontFamily: "monospace", color: "var(--octopus-ice)" }}>{cm.sku}</div>
                        <div style={{ fontSize: "0.78rem", color: "var(--octopus-voltage)", opacity: 0.65, marginTop: "0.2rem" }}>
                          {cm.description}
                        </div>
                        <div style={{ marginTop: "0.35rem" }}>
                          <Badge
                            bg="rgba(251,146,60,0.15)" fg="#fb923c" border="#fb923c"
                            label={`${(sim * 100).toFixed(1)}% similar`}
                          />
                        </div>
                      </>
                    ) : (
                      <span style={{ color: "var(--octopus-voltage)", opacity: 0.6, fontStyle: "italic" }}>
                        No similar SKU on file
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "0.6rem 0.5rem", verticalAlign: "top" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
                      <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", cursor: "pointer" }}>
                        <input
                          type="radio"
                          name={`decision-${idx}`}
                          checked={d.action === "approve_new"}
                          onChange={() => setDecision(idx, { action: "approve_new" })}
                          data-testid={`decision-new-${idx}`}
                        />
                        <span>Approve as new SKU</span>
                      </label>
                      <label style={{ display: "flex", alignItems: "center", gap: "0.4rem", cursor: cm ? "pointer" : "not-allowed", opacity: cm ? 1 : 0.6 }}>
                        <input
                          type="radio"
                          name={`decision-${idx}`}
                          checked={d.action === "fix"}
                          onChange={() => setDecision(idx, { action: "fix" })}
                          data-testid={`decision-fix-${idx}`}
                        />
                        <span>Map to existing SKU:</span>
                        <input
                          type="text"
                          value={d.canonical_sku || ""}
                          onChange={e => setDecision(idx, { action: "fix", canonical_sku: e.target.value })}
                          placeholder={cm?.sku || "type SKU..."}
                          data-testid={`canonical-sku-${idx}`}
                          style={{
                            background: "rgba(0,0,0,0.3)", border: "1px solid rgba(96,240,248,0.2)",
                            borderRadius: "6px", padding: "0.3rem 0.5rem", color: "var(--octopus-ice)",
                            fontFamily: "monospace", fontSize: "0.82rem", minWidth: "140px",
                          }}
                          onFocus={() => setDecision(idx, { action: "fix" })}
                        />
                      </label>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: "1rem", gap: "0.5rem" }}>
        {confirmDelete ? (
          <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <span style={{ fontSize: "0.85rem", color: "#fb923c" }}>Delete this submission?</span>
            <Button
              onClick={handleDelete}
              disabled={deleting}
              data-testid={`delete-confirm-${sub.id}`}
              style={{
                background: "rgba(239,68,68,0.15)", border: "1px solid #ef4444",
                color: "#ef4444", padding: "0.3rem 0.75rem", borderRadius: "6px",
                cursor: deleting ? "not-allowed" : "pointer", fontSize: "0.82rem",
              }}
            >
              {deleting ? "Deleting…" : "Yes, delete"}
            </Button>
            <Button
              onClick={() => setConfirmDelete(false)}
              disabled={deleting}
              data-testid={`delete-cancel-${sub.id}`}
              style={{
                background: "transparent", border: "1px solid rgba(96,240,248,0.2)",
                color: "var(--octopus-voltage)", padding: "0.3rem 0.75rem",
                borderRadius: "6px", cursor: "pointer", fontSize: "0.82rem",
              }}
            >
              Cancel
            </Button>
          </div>
        ) : (
          <Button
            onClick={() => setConfirmDelete(true)}
            data-testid={`delete-submission-${sub.id}`}
            style={{
              display: "flex", alignItems: "center", gap: "0.35rem",
              background: "transparent", border: "1px solid rgba(239,68,68,0.3)",
              color: "#f87171", padding: "0.3rem 0.75rem", borderRadius: "6px",
              cursor: "pointer", fontSize: "0.82rem",
            }}
          >
            <Trash2 size={14} />
            Delete submission
          </Button>
        )}

        <div style={{ display: "flex", gap: "0.5rem" }}>
          <Button
            onClick={handleApproveAllNew}
            disabled={approvingAll || submitting}
            data-testid={`approve-all-new-${sub.id}`}
            style={{ background: "rgba(24,240,184,0.12)", border: "1px solid #18F0B8", color: "#18F0B8", padding: "0.4rem 0.9rem", borderRadius: "8px", cursor: "pointer", fontSize: "0.85rem", fontWeight: 600 }}
          >
            {approvingAll ? "Approving…" : `Approve all ${flaggedRows.length} as new`}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!ready || submitting}
            className="btn btn-primary"
            data-testid={`resolve-submit-${sub.id}`}
          >
            {submitting ? "Submitting…" : "Submit reconciliation"}
          </Button>
        </div>
      </div>
    </div>
  );
}
